import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';

import { ClockIcon, ExclamationTriangleIcon, EyeIcon } from '@heroicons/react/24/outline';

import { PageContainer, Spinner } from '@shared';

import { OrderActionResultDialog } from '@/components/OrderActionResultDialog';
import { useAuth } from '@/features/auth/useAuth';
import { useLocale, useTranslations } from '@/i18n/client';
import { errorKeyOf, type ApiErrorKey } from '@/services/api/errors';
import { OrdersApi } from '@/services/api/orders-api';
import type { OrderFlowState, ProjectOrderDetails, ProjectOrderFlowStep } from '@/services/api/types';
import { parseUtcDate } from '@/utils/date-time';
import { isAbortError } from './orders-page-utils';

import { OrderNotes } from '@/features/orders/OrderNotes';
import { AdditionalFilesUpload } from '@/features/orders/AdditionalFilesUpload';
import { ScanFilesSection, type ScanFileItem } from '@/features/orders/ScanFilesSection';
import {
  flowStatesForOrder,
  isDesignReviewState,
  isScanRevisionState,
  isScanRevisionUploadOnlyState,
} from './order-status';

interface OrderDetailsRouteState {
  readonly order?: {
    readonly id: string;
    readonly orderNumber: string;
    readonly created: string;
    readonly projectName: string;
    readonly orderState: string;
  };
}

interface FlowStepItem {
  readonly key: string;
  readonly label: string;
}

interface OrderDetailsModel {
  readonly id: string;
  readonly orderNumber: string;
  readonly status: string;
  readonly serviceName: string;
  readonly customerReference: string;
  readonly created: string;
  readonly flowSteps: readonly FlowStepItem[];
  readonly toothColor: string;
  readonly shippingMethod: string;
  readonly deliveryDate: string;
  readonly finishingWishes: readonly string[];
  readonly scanFiles: readonly ScanFileItem[];
  readonly internalCaseId: string;
  readonly notes: string;
  readonly priceLabel: string;
  readonly canCancel: boolean;
  readonly canUploadAdditionalFiles: boolean;
}

function normalizeStatus(state: string): string {
  return state
    .trim()
    .toLocaleLowerCase('de')
    .replace(/[_\s-]+/g, '');
}

function statusKeyOf(orderState: string): string {
  const state = normalizeStatus(orderState);

  switch (state) {
    case 'created':
    case 'angelegt':
      return 'created';
    case 'scanned':
    case 'gescannt':
      return 'scanned';
    case 'design':
    case 'designed':
    case 'konstruiert':
      return 'designed';
    case 'customerdesignreview':
    case 'designreview':
    case 'designfreigabe':
      return 'designReview';
    case 'toconfirm':
    case 'unbestätigt':
    case 'unbestaetigt':
      return 'toConfirm';
    case 'verification':
    case 'inprüfung':
    case 'inpruefung':
      return 'verification';
    case 'scanrevision':
      return 'scanRevision';
    case 'approved':
    case 'freigegeben':
      return 'approved';
    case 'inprogress':
    case 'inbearbeitung':
      return 'inProgress';
    case 'inproduction':
    case 'infertigung':
      return 'inProduction';
    case 'qualitycheck':
    case 'qualitätskontrolle':
    case 'qualitaetskontrolle':
      return 'qualityCheck';
    case 'readydispatch':
    case 'versandfertig':
      return 'readyDispatch';
    case 'intransit':
    case 'versandt':
    case 'versendet':
      return 'inTransit';
    case 'completed':
    case 'abgeschlossen':
      return 'completed';
    case 'revision':
    case 'überarbeitung':
    case 'ueberarbeitung':
      return 'revision';
    case 'canceled':
    case 'cancelled':
    case 'storniert':
      return 'canceled';
    case 'uploading':
      return 'uploading';
    case 'none':
      return 'none';
    case 'undefined':
      return 'none';
    default:
      return state || 'none';
  }
}

function statusLabelOf(key: string, tOrders: ReturnType<typeof useTranslations>): string {
  switch (key) {
    case 'created':
      return tOrders('status.created');
    case 'scanned':
      return tOrders('status.scanned');
    case 'designed':
      return tOrders('status.designed');
    case 'designReview':
      return tOrders('status.designReview');
    case 'toConfirm':
      return tOrders('status.toConfirm');
    case 'verification':
      return tOrders('status.verification');
    case 'scanRevision':
      return tOrders('status.scanRevision');
    case 'approved':
      return tOrders('status.approved');
    case 'inProgress':
      return tOrders('status.inProgress');
    case 'inProduction':
      return tOrders('status.inProduction');
    case 'qualityCheck':
      return tOrders('status.qualityCheck');
    case 'readyDispatch':
      return tOrders('status.readyDispatch');
    case 'inTransit':
      return tOrders('status.inTransit');
    case 'completed':
      return tOrders('status.completed');
    case 'revision':
      return tOrders('status.revision');
    case 'canceled':
      return tOrders('status.canceled');
    case 'uploading':
      return tOrders('status.uploading');
    case 'none':
    default:
      return '';
  }
}

function statusTooltipOf(key: string, tOrders: ReturnType<typeof useTranslations>): string {
  switch (normalizeStatus(key)) {
    case 'uploading':
      return tOrders('statusTooltips.uploading');
    case 'toconfirm':
      return tOrders('statusTooltips.toConfirm');
    case 'verification':
      return tOrders('statusTooltips.verification');
    case 'scanrevision':
      return tOrders('statusTooltips.scanRevision');
    case 'design':
    case 'designed':
    case 'konstruiert':
      return tOrders('statusTooltips.design');
    case 'designcheck':
    case 'designqualitycheck':
      return tOrders('statusTooltips.designCheck');
    case 'customerdesignreview':
    case 'designreview':
    case 'designfreigabe':
      return tOrders('statusTooltips.designReview');
    case 'designready':
      return tOrders('statusTooltips.designReady');
    case 'approved':
    case 'freigegeben':
      return tOrders('statusTooltips.approved');
    case 'inprogress':
    case 'inbearbeitung':
      return tOrders('statusTooltips.inProgress');
    case 'inproduction':
    case 'infertigung':
      return tOrders('statusTooltips.inProduction');
    case 'qualitycheck':
    case 'qualitätskontrolle':
    case 'qualitaetskontrolle':
      return tOrders('statusTooltips.qualityCheck');
    case 'readydispatch':
    case 'versandfertig':
      return tOrders('statusTooltips.readyDispatch');
    case 'intransit':
    case 'versandt':
    case 'versendet':
      return tOrders('statusTooltips.inTransit');
    case 'completed':
    case 'abgeschlossen':
      return tOrders('statusTooltips.completed');
    case 'canceled':
    case 'cancelled':
    case 'storniert':
      return tOrders('statusTooltips.canceled');
    default:
      return '';
  }
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parseUtcDate(value));
}

function formatToothColor(value: string): string {
  return value
    .trim()
    .replace(/^vita(?:[_\s-]+)?/i, '')
    .replace(/_/g, ' ')
    .trim()
    .toUpperCase();
}

function formatFileSize(sizeInKB: number | null | undefined): string {
  if (typeof sizeInKB !== 'number') {
    return '-';
  }

  const numberFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 });
  if (sizeInKB < 1024) {
    return `${numberFormat.format(sizeInKB)} KB`;
  }

  return `${numberFormat.format(sizeInKB / 1024)} MB`;
}

function fallbackDetails(orderId: string): OrderDetailsModel {
  const created = '2026-08-03T06:43:00.000Z';
  return {
    id: orderId,
    orderNumber: 'A.20260801',
    status: 'verification',
    serviceName: 'M-DENTURE Basic - Nur Ober- und Unterkieferprothese bestellbar',
    customerReference: '22142453',
    created,
    flowSteps: [],
    toothColor: 'A2',
    shippingMethod: 'Standardversand',
    deliveryDate: 'Noch nicht verbindlich bestaetigt',
    finishingWishes: ['Anatomische Gestaltung der Prothesenbasis im Frontzahnbereich'],
    scanFiles: [
      {
        name: '2022-11-17-104346-aestheticplate.stl',
        type: 'STL',
        size: '42,3 MB',
        uploadedAt: created,
        isAdditionalFile: false,
        isProtocolFile: false,
        isScanFile: true,
      },
      {
        name: '2022-11-17-104346-lowerjaw.stl',
        type: 'STL',
        size: '10,3 MB',
        uploadedAt: created,
        isAdditionalFile: false,
        isProtocolFile: false,
        isScanFile: true,
      },
      {
        name: '2022-11-17-104346-upperjaw.stl',
        type: 'STL',
        size: '13,4 MB',
        uploadedAt: created,
        isAdditionalFile: false,
        isProtocolFile: false,
        isScanFile: true,
      },
    ],
    internalCaseId: '57',
    notes: 'Keine zusaetzlichen Hinweise hinterlegt.',
    priceLabel: '690 EUR zzgl. MwSt.',
    canCancel: false,
    canUploadAdditionalFiles: true,
  };
}

function flowStepItemOf(key: string, displayName: string): FlowStepItem | null {
  const mappedKey = statusKeyOf(key);

  if (mappedKey === 'none' || mappedKey === 'uploading') {
    return null;
  }

  return {
    key: mappedKey,
    label: displayName?.trim() || key.trim() || mappedKey,
  };
}

function flowStepsFromServer(steps: readonly ProjectOrderFlowStep[] | undefined): FlowStepItem[] {
  return [...(steps ?? [])]
    .sort((left, right) => left.sequenceNumber - right.sequenceNumber)
    .map((step) => flowStepItemOf(step.key, step.displayName))
    .filter((step): step is FlowStepItem => step !== null);
}

function flowStepsFromConfiguredStates(
  states: readonly OrderFlowState[],
  orderType: string,
  currentOrderState: string,
): FlowStepItem[] {
  const currentStateKey = statusKeyOf(currentOrderState);

  return flowStatesForOrder(states, orderType)
    .filter(
      (state) =>
        state.isEnabled &&
        (state.isActive ||
          statusKeyOf(state.name) === 'completed' ||
          statusKeyOf(state.name) === currentStateKey),
    )
    .sort((left, right) => left.sequenceNumber - right.sequenceNumber)
    .map((state) => flowStepItemOf(state.name, state.displayName))
    .filter((step): step is FlowStepItem => step !== null);
}

function flowStepsForOrder(
  states: readonly OrderFlowState[],
  orderType: string,
  fallbackSteps: readonly ProjectOrderFlowStep[] | undefined,
  currentOrderState: string,
): FlowStepItem[] {
  const configuredSteps = flowStepsFromConfiguredStates(states, orderType, currentOrderState);
  return configuredSteps.length > 0 ? configuredSteps : flowStepsFromServer(fallbackSteps);
}

function mapOrderType(type: string, t: ReturnType<typeof useTranslations>): string {
  const normalized = type.trim().toLocaleLowerCase('de');
  if (normalized === 'designproduction') return t('orderTypes.designProduction');
  if (normalized === 'addononlyorder') return t('orderTypes.addonOnlyOrder');
  if (normalized === 'bulkupload') return t('orderTypes.bulkUpload');
  if (normalized === 'credits') return t('orderTypes.credits');
  return t('orderTypes.production');
}

function isRelevantProductionScanFile(fileName: string): boolean {
  const normalizedName = fileName.trim();
  return (
    normalizedName.toLocaleLowerCase('en-US').endsWith('.stl') &&
    !/(?:[_-](?:cover|load|model))\.stl$/i.test(normalizedName)
  );
}

function isHiddenProjectFile(fileName: string): boolean {
  const normalizedName = fileName.trim().toLocaleLowerCase('en-US');
  return normalizedName.endsWith('.constructioninfo') || normalizedName.endsWith('.manager360');
}

function isVisibleCustomerFile(file: Pick<ScanFileItem, 'name' | 'isScanFile'>): boolean {
  return file.isScanFile === true ? isRelevantProductionScanFile(file.name) : !isHiddenProjectFile(file.name);
}

function mapDeliveryMethod(method: string, t: ReturnType<typeof useTranslations>): string {
  const normalized = method.trim().toLocaleLowerCase('de');
  if (normalized === 'express') return t('deliveryMethods.express');
  if (normalized === 'pickup') return t('deliveryMethods.pickup');
  return t('deliveryMethods.standard');
}

function mergeServerDetails(
  base: OrderDetailsModel,
  server: ProjectOrderDetails,
  t: ReturnType<typeof useTranslations>,
  configuredFlowSteps?: readonly FlowStepItem[],
): OrderDetailsModel {
  const serviceCurrency = server.serviceCurrency ?? 'EUR';
  const servicePriceValue = typeof server.servicePrice === 'number' ? server.servicePrice : null;
  const serviceVatValue = typeof server.serviceVat === 'number' ? server.serviceVat : null;
  const hasServicePrice = servicePriceValue !== null;
  const hasServiceVat = serviceVatValue !== null;
  const priceLabel = hasServicePrice
    ? `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(servicePriceValue)} ${serviceCurrency}${
        hasServiceVat
          ? ` zzgl. ${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(serviceVatValue)} % MwSt.`
          : ''
      }`
    : base.priceLabel;

  const scanFiles = (server.scanFiles ?? [])
    .map((file) => {
      const isProtocolFile = file.isProtocolFile === true;
      const isAdditionalFile = !isProtocolFile && file.isAdditionalFile === true;

      return {
        ...(file.id ? { id: file.id } : {}),
        name: file.name,
        type: file.type,
        size: formatFileSize(file.sizeInKB),
        uploadedAt: file.uploadedAtUtc,
        isAdditionalFile,
        isProtocolFile,
        isScanFile: !isProtocolFile && !isAdditionalFile && file.isScanFile !== false,
      };
    })
    .filter(isVisibleCustomerFile);
  const flowSteps =
    configuredFlowSteps && configuredFlowSteps.length > 0
      ? configuredFlowSteps
      : base.flowSteps.length > 0
        ? base.flowSteps
        : flowStepsFromServer(server.flowSteps);

  return {
    ...base,
    id: server.id,
    orderNumber: server.orderNumber || base.orderNumber,
    status: statusKeyOf(server.orderState),
    created: server.created,
    serviceName: server.serviceName || mapOrderType(server.orderType, t),
    customerReference:
      server.customerReference || server.projectName || server.orderNumber || base.customerReference,
    flowSteps,
    toothColor: formatToothColor(server.toothColor || t('detailsSection.toothColorUnknown')),
    shippingMethod: mapDeliveryMethod(server.deliveryMethod, t),
    deliveryDate: server.deliveryDateUtc
      ? formatDateTime(server.deliveryDateUtc)
      : t('detailsSection.deliveryDateUnknown'),
    finishingWishes:
      (server.finishingWishes ?? []).length > 0
        ? (server.finishingWishes ?? [])
        : [t('detailsSection.finishingWishesNone')],
    scanFiles,
    internalCaseId: server.internalCaseId || base.internalCaseId,
    notes: server.notes || t('notesSection.emptyBody'),
    priceLabel,
    canCancel: server.canCancel === true,
    canUploadAdditionalFiles: server.canUploadFiles === true,
  };
}

export function OrderDetailsPage() {
  const t = useTranslations('common.orderDetails');
  const tOrders = useTranslations('common.orders');
  const tErrors = useTranslations('errors.api');
  const tStatus = useTranslations('common.status');
  const locale = useLocale();
  const { client } = useAuth();
  const { orderId } = useParams();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(() => Boolean(client));
  const [errorKey, setErrorKey] = useState<ApiErrorKey | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelErrorKey, setCancelErrorKey] = useState<ApiErrorKey | null>(null);
  const [isCancelResultOpen, setIsCancelResultOpen] = useState(false);

  const routeState = location.state as OrderDetailsRouteState | null;

  const initialDetails = useMemo<OrderDetailsModel>(() => {
    const fallback = fallbackDetails(orderId ?? 'demo-order');
    const selectedOrder = routeState?.order;
    const shouldStartWithPendingService = Boolean(client && orderId);

    const detailsBase = shouldStartWithPendingService
      ? {
          ...fallback,
          serviceName: '',
        }
      : fallback;

    if (!selectedOrder) {
      return {
        ...detailsBase,
        flowSteps: [],
      };
    }

    return {
      ...detailsBase,
      id: selectedOrder.id,
      orderNumber: selectedOrder.orderNumber,
      status: statusKeyOf(selectedOrder.orderState),
      created: selectedOrder.created,
      customerReference: selectedOrder.projectName || selectedOrder.orderNumber,
      flowSteps: [],
    };
  }, [client, orderId, routeState, t]);

  const [details, setDetails] = useState<OrderDetailsModel>(() => initialDetails);

  useEffect(() => {
    if (!client || !orderId) {
      return;
    }

    const abort = new AbortController();
    const api = new OrdersApi(client);

    void Promise.all([api.getProjectOrder(orderId, abort.signal), api.getFlowStates(locale, abort.signal)])
      .then(([serverOrder, flowStates]) => {
        setDetails((previous) =>
          mergeServerDetails(
            previous,
            serverOrder,
            t,
            flowStepsForOrder(
              flowStates,
              serverOrder.orderType,
              serverOrder.flowSteps,
              serverOrder.orderState,
            ),
          ),
        );
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }
        setErrorKey(errorKeyOf(error));
      })
      .finally(() => {
        setIsLoading(false);
      });

    return () => {
      abort.abort();
    };
  }, [client, locale, orderId, t]);

  const refreshDetails = async () => {
    if (!client || !orderId) {
      return;
    }

    const api = new OrdersApi(client);
    const serverOrder = await api.getProjectOrder(orderId);
    setDetails((previous) => mergeServerDetails(previous, serverOrder, t));
  };

  const handleCancel = async () => {
    if (!client || !orderId || !details.canCancel || isCancelling) {
      return;
    }

    if (!window.confirm(t('cancelSection.confirmation'))) {
      return;
    }

    setIsCancelling(true);
    setCancelErrorKey(null);
    setIsCancelResultOpen(false);

    try {
      const api = new OrdersApi(client);
      await api.cancelOrder(orderId);
      const cancelledOrder = await api.getProjectOrder(orderId);
      setDetails((previous) => mergeServerDetails(previous, cancelledOrder, t));
      setIsCancelResultOpen(true);
    } catch (error) {
      if (!isAbortError(error)) {
        setCancelErrorKey(errorKeyOf(error));
      }
    } finally {
      setIsCancelling(false);
    }
  };

  const activeFlowStep = details.flowSteps.find((step) => step.key === details.status);
  const statusLabel = activeFlowStep?.label || statusLabelOf(details.status, tOrders);
  const statusTooltip = statusTooltipOf(details.status, tOrders);
  const isReviewRequired = isDesignReviewState(details.status);
  const isScanRevision = isScanRevisionState(details.status);
  const isScanUploadOnly = isScanRevisionUploadOnlyState(details.status);
  const isCustomerActionRequired = isReviewRequired || isScanRevision;
  const actionEyebrow = isScanUploadOnly
    ? t('scanUploadSection.eyebrow')
    : isScanRevision
      ? t('scanRevisionSection.eyebrow')
      : t('reviewSection.eyebrow');
  const actionTitle = isScanUploadOnly
    ? t('scanUploadSection.title')
    : isScanRevision
      ? t('scanRevisionSection.title')
      : t('reviewSection.title');
  const actionDescription = isScanUploadOnly
    ? t('scanUploadSection.description')
    : isScanRevision
      ? t('scanRevisionSection.description')
      : t('reviewSection.description');
  const actionDelayHint = isScanUploadOnly
    ? t('scanUploadSection.delayHint')
    : isScanRevision
      ? t('scanRevisionSection.delayHint')
      : t('reviewSection.delayHint');
  const actionLabel = isScanUploadOnly
    ? tOrders('scanUploadAction')
    : isScanRevision
      ? tOrders('scanRevisionAction')
      : tOrders('reviewAction');
  const hasLoadedService = details.serviceName.trim().length > 0;
  const showFullPageSpinner = Boolean(client && (isLoading || (!hasLoadedService && !errorKey)));

  if (showFullPageSpinner) {
    return (
      <PageContainer className="md-order-details-page max-sm:w-full">
        <div className="md-order-details-page__content mt-6 flex flex-col gap-4 max-sm:mt-4 max-sm:gap-3">
          <section className="rounded-[var(--zs-radius-m)] border border-[var(--md-border)] bg-[var(--md-white)] p-6 shadow-[var(--md-shadow-soft)] max-sm:p-4">
            <div className="flex items-center justify-center py-2">
              <Spinner label={tStatus('loading')} />
            </div>
          </section>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="md-order-details-page max-sm:w-full">
      <div className="md-order-details-page__content mt-6 flex flex-col gap-4 max-sm:mt-4 max-sm:gap-3">
        <section className="rounded-[var(--zs-radius-m)] border border-[var(--md-border)] bg-[var(--md-white)] p-6 shadow-[var(--md-shadow-soft)] max-sm:p-4">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3 max-sm:flex-col max-sm:gap-3">
            <span className="inline-flex items-center rounded-full bg-[var(--md-primary-light)] px-3 py-1 text-xs font-bold tracking-[0.04em] text-[var(--md-primary)] uppercase">
              {t('eyebrow')}
            </span>
            <Link to="/cases" className="shrink-0">
              {t('actions.backToOrders')}
            </Link>
          </div>
          <h1 className="mt-3 mb-2 text-[clamp(1.7rem,4vw,2.2rem)]">{details.orderNumber}</h1>
          <p className="m-0 text-[var(--zs-muted)]">{t('description')}</p>
          {isLoading ? <p className="mt-2 text-sm text-[var(--zs-muted)]">{tStatus('loading')}</p> : null}
          {errorKey ? <p className="mt-2 text-sm text-[var(--md-highlight)]">{tErrors(errorKey)}</p> : null}

          {isCustomerActionRequired ? (
            <section
              className="md-order-review grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-6 p-[1.35rem_1.5rem] max-md:grid-cols-[auto_minmax(0,1fr)] max-md:gap-4 max-md:p-[1.15rem] max-sm:grid-cols-1 max-sm:gap-[0.85rem] max-sm:p-4"
              aria-labelledby={
                isScanUploadOnly
                  ? 'order-scan-upload-required-title'
                  : isScanRevision
                    ? 'order-scan-revision-required-title'
                    : 'order-review-required-title'
              }
              role="status"
            >
              <div
                className="md-order-review__icon h-16 w-16 max-sm:h-[3.25rem] max-sm:w-[3.25rem]"
                aria-hidden="true"
              >
                <ExclamationTriangleIcon />
              </div>
              <div className="md-order-review__content">
                <span className="md-review__section-eyebrow">{actionEyebrow}</span>
                <h2
                  id={
                    isScanUploadOnly
                      ? 'order-scan-upload-required-title'
                      : isScanRevision
                        ? 'order-scan-revision-required-title'
                        : 'order-review-required-title'
                  }
                >
                  {actionTitle}
                </h2>
                <p>{actionDescription}</p>
                <div className="md-order-review__delay">
                  <ClockIcon aria-hidden="true" />
                  <span>{actionDelayHint}</span>
                </div>
              </div>
              <div className="md-order-review__action flex min-w-52 items-center border-l border-l-[color-mix(in_srgb,var(--md-footer-text)_45%,transparent)] pl-6 max-md:col-span-full max-md:min-w-0 max-md:border-l-0 max-md:border-t max-md:border-t-[color-mix(in_srgb,var(--md-footer-text)_45%,transparent)] max-md:pt-4 max-md:pl-0 max-sm:col-span-1 max-sm:w-full max-sm:pt-3">
                <Link
                  className="zs-button md-order-review__button w-full whitespace-nowrap max-sm:whitespace-normal"
                  to={`/cases/${details.id}/${isScanRevision ? 'scan-revision' : 'review'}`}
                  aria-label={`${actionLabel} ${details.orderNumber}`}
                  title={actionLabel}
                >
                  <EyeIcon className="block h-5 w-5 shrink-0" aria-hidden="true" />
                  <span>{actionLabel}</span>
                </Link>
              </div>
            </section>
          ) : null}

          <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className="flex flex-col gap-1.5 rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-bg)] px-4 py-3.5">
              <span>{t('stats.status')}</span>
              <strong className="leading-[1.35] text-[var(--md-primary)]" title={statusTooltip || undefined}>
                {statusLabel}
              </strong>
            </div>
            <div className="flex flex-col gap-1.5 rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-bg)] px-4 py-3.5">
              <span>{t('stats.service')}</span>
              <strong className="leading-[1.35] text-[var(--md-primary)]">{details.serviceName}</strong>
            </div>
            <div className="flex flex-col gap-1.5 rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-bg)] px-4 py-3.5">
              <span>{t('stats.customerReference')}</span>
              <strong className="leading-[1.35] text-[var(--md-primary)]">{details.customerReference}</strong>
            </div>
            <div className="flex flex-col gap-1.5 rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-bg)] px-4 py-3.5">
              <span>{t('stats.createdAt')}</span>
              <strong className="leading-[1.35] text-[var(--md-primary)]">
                {formatDateTime(details.created)}
              </strong>
            </div>
          </div>
        </section>

        <section className="rounded-[var(--zs-radius-m)] border border-[var(--md-border)] bg-[var(--md-white)] p-6 shadow-[var(--md-shadow-soft)]">
          <div className="mb-3.5 flex flex-wrap items-baseline gap-2.5">
            <span className="text-sm text-[var(--zs-muted)]">{t('currentStatusLabel')}</span>
            <strong className="text-base text-[var(--md-primary)]" title={statusTooltip || undefined}>
              {statusLabel}
            </strong>
          </div>

          <div
            className="grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-2.5"
            role="list"
            aria-label={t('flowLabel')}
          >
            {details.flowSteps.map((step) => {
              const active = step.key === details.status;
              return (
                <div
                  key={step.key}
                  className={`rounded-[var(--zs-radius-s)] border px-3 py-2 text-[0.8125rem] ${
                    active
                      ? 'border-[color-mix(in_oklab,var(--md-highlight)_55%,var(--md-primary))] bg-[color-mix(in_oklab,var(--md-highlight)_18%,var(--md-white))] font-bold text-[var(--md-primary)]'
                      : 'border-[var(--md-border)] bg-[var(--md-bg)] text-[var(--zs-muted)]'
                  }`}
                  role="listitem"
                  title={statusTooltipOf(step.key, tOrders) || undefined}
                >
                  {step.label}
                </div>
              );
            })}
          </div>
        </section>

        <AdditionalFilesUpload
          orderId={details.id}
          enabled={Boolean(client && !errorKey && details.canUploadAdditionalFiles)}
          onUploaded={refreshDetails}
        />

        <section className="rounded-[var(--zs-radius-m)] border border-[var(--md-border)] bg-[var(--md-white)] p-6 shadow-[var(--md-shadow-soft)]">
          <h2>{t('serviceSection.title')}</h2>
          <div className="mt-3 flex flex-col gap-3 rounded-[var(--zs-radius-s)] border border-[var(--md-border)] p-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="font-bold leading-[1.4] text-[var(--md-primary)]">{details.serviceName}</div>
            </div>
            <span className="inline-flex w-fit items-center whitespace-nowrap rounded-full bg-[var(--md-primary-light)] px-2.5 py-1 text-xs font-bold text-[var(--md-primary)]">
              {details.priceLabel}
            </span>
          </div>
        </section>

        <section className="rounded-[var(--zs-radius-m)] border border-[var(--md-border)] bg-[var(--md-white)] p-6 shadow-[var(--md-shadow-soft)]">
          <h2>{t('detailsSection.title')}</h2>
          <table className="md-order-details__key-value-table w-full border-collapse">
            <tbody>
              <tr>
                <th className="w-[34%] border-b border-[var(--md-border)] px-3 py-3 text-left align-top text-[0.93rem] font-bold text-[var(--md-primary)]">
                  {t('detailsSection.toothColor')}
                </th>
                <td className="border-b border-[var(--md-border)] px-3 py-3 text-left align-top text-[0.93rem]">
                  {details.toothColor}
                </td>
              </tr>
              <tr>
                <th className="w-[34%] border-b border-[var(--md-border)] px-3 py-3 text-left align-top text-[0.93rem] font-bold text-[var(--md-primary)]">
                  {t('detailsSection.shippingMethod')}
                </th>
                <td className="border-b border-[var(--md-border)] px-3 py-3 text-left align-top text-[0.93rem]">
                  {details.shippingMethod}
                </td>
              </tr>
              <tr>
                <th className="w-[34%] border-b border-[var(--md-border)] px-3 py-3 text-left align-top text-[0.93rem] font-bold text-[var(--md-primary)]">
                  {t('detailsSection.deliveryDate')}
                </th>
                <td className="border-b border-[var(--md-border)] px-3 py-3 text-left align-top text-[0.93rem]">
                  {details.deliveryDate}
                </td>
              </tr>
              <tr>
                <th className="w-[34%] px-3 py-3 text-left align-top text-[0.93rem] font-bold text-[var(--md-primary)]">
                  {t('detailsSection.finishingWishes')}
                </th>
                <td className="px-3 py-3 text-left align-top text-[0.93rem]">
                  <ul className="m-0 list-none pl-0">
                    {details.finishingWishes.map((wish) => (
                      <li key={wish}>{wish}</li>
                    ))}
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        <ScanFilesSection
          orderId={details.id}
          files={details.scanFiles}
          canUploadAdditionalFiles={details.canUploadAdditionalFiles}
        />

        <section className="grid grid-cols-1 gap-5 rounded-[var(--zs-radius-m)] border border-[var(--md-border)] bg-[var(--md-white)] p-6 shadow-[var(--md-shadow-soft)] lg:grid-cols-2">
          <div>
            <h2>{t('orderDataSection.title')}</h2>
            <table className="md-order-details__key-value-table w-full border-collapse">
              <tbody>
                <tr>
                  <th className="w-[34%] border-b border-[var(--md-border)] px-3 py-3 text-left align-top text-[0.93rem] font-bold text-[var(--md-primary)]">
                    {t('orderDataSection.systemOrderNumber')}
                  </th>
                  <td className="border-b border-[var(--md-border)] px-3 py-3 text-left align-top text-[0.93rem]">
                    {details.orderNumber}
                  </td>
                </tr>
                <tr>
                  <th className="w-[34%] border-b border-[var(--md-border)] px-3 py-3 text-left align-top text-[0.93rem] font-bold text-[var(--md-primary)]">
                    {t('orderDataSection.internalCaseId')}
                  </th>
                  <td className="border-b border-[var(--md-border)] px-3 py-3 text-left align-top text-[0.93rem]">
                    {details.internalCaseId}
                  </td>
                </tr>
                <tr>
                  <th className="w-[34%] border-b border-[var(--md-border)] px-3 py-3 text-left align-top text-[0.93rem] font-bold text-[var(--md-primary)]">
                    {t('orderDataSection.customerReference')}
                  </th>
                  <td className="border-b border-[var(--md-border)] px-3 py-3 text-left align-top text-[0.93rem]">
                    {details.customerReference}
                  </td>
                </tr>
                <tr>
                  <th className="w-[34%] px-3 py-3 text-left align-top text-[0.93rem] font-bold text-[var(--md-primary)]">
                    {t('orderDataSection.status')}
                  </th>
                  <td className="px-3 py-3 text-left align-top text-[0.93rem]">
                    <span
                      className="inline-flex items-center whitespace-nowrap rounded-full bg-[var(--md-primary-light)] px-2.5 py-1 text-xs font-bold text-[var(--md-primary)]"
                      title={statusTooltip || undefined}
                    >
                      {statusLabel}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h2>{t('notesSection.title')}</h2>
            <p className="m-0 text-[var(--zs-muted)]">{details.notes}</p>
          </div>
        </section>

        <OrderNotes />

        <section className="rounded-[var(--zs-radius-m)] border border-[var(--md-border)] border-l-4 border-l-[color-mix(in_oklab,var(--md-highlight)_50%,var(--md-primary))] bg-[var(--md-white)] p-6 shadow-[var(--md-shadow-soft)] max-sm:p-4">
          <h2>{t('cancelSection.title')}</h2>
          <p className="m-0 text-[var(--zs-muted)]">{t('cancelSection.description')}</p>
          {cancelErrorKey ? (
            <p className="mt-2 text-sm text-[var(--md-highlight)]">{tErrors(cancelErrorKey)}</p>
          ) : null}
          <button
            type="button"
            className="md-order-details__cancel-button zs-button mt-4 w-full border-[color-mix(in_oklab,var(--md-highlight)_55%,var(--md-primary))] bg-[color-mix(in_oklab,var(--md-highlight)_55%,var(--md-primary))] hover:border-[var(--md-primary-dark)] hover:bg-[var(--md-primary-dark)] focus-visible:border-[var(--md-primary-dark)] focus-visible:bg-[var(--md-primary-dark)] sm:w-auto"
            disabled={!details.canCancel || isCancelling}
            onClick={() => void handleCancel()}
          >
            {isCancelling ? t('cancelSection.busy') : t('cancelSection.action')}
          </button>
        </section>
      </div>
      {isCancelResultOpen ? (
        <OrderActionResultDialog
          title={t('cancelSection.successTitle')}
          message={t('cancelSection.successDescription')}
          actionLabel={t('cancelSection.successAction')}
          to={`/cases/${details.id}`}
          onAction={() => setIsCancelResultOpen(false)}
        />
      ) : null}
    </PageContainer>
  );
}
